import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SEC_ApiCall, SEC_DataApiPost } from '../func.js';
import './PerformanceManagement.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const PHASES = [
  { id: 'OBJECTIVE_SETTING', label: 'Objectives', section: 'objectiveSetting' },
  { id: 'MONITORING', label: 'Monitoring', section: 'monitoring' },
  { id: 'FINAL_REVIEW', label: 'Final review', section: 'finalReview' },
  { id: 'CPD', label: 'CPD log', section: 'cpd' },
];

/** Render and update all stages of one Performance Management record. */
export default function PerformanceManagementRecordView() {
  const { recordId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('OBJECTIVE_SETTING');
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState('');
  const [actionComment, setActionComment] = useState('');
  const [selectedReviewId, setSelectedReviewId] = useState('');

  async function loadRecord() {
    setLoading(true);
    try {
      const response = await SEC_ApiCall('perfmgr/record', { recordId });
      if (!response || response.message) throw new Error(response?.message || 'Unable to load record');
      setData(response);
      setPhase(response.header?.currentPhase === 'COMPLETED' ? 'FINAL_REVIEW' : response.header?.currentPhase || 'OBJECTIVE_SETTING');
      setDrafts(buildDrafts(response));
      setSelectedReviewId((current) => current || response.schedule?.monitoringWindows?.[0]?.reviewId || '');
    } catch (error) {
      console.error(error);
      toast.error('❌ Unable to load this performance record.', TOAST_CONFIG);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadRecord(); }, [recordId]); // eslint-disable-line react-hooks/exhaustive-deps

  const phaseConfig = PHASES.find((item) => item.id === phase) || PHASES[0];
  const fields = data?.template?.sections?.[phaseConfig.section] || [];
  const reviewWindow = data?.schedule?.monitoringWindows?.find((window) => window.reviewId === selectedReviewId) || data?.schedule?.monitoringWindows?.[0] || {};
  const items = useMemo(() => {
    if (!data) return [];
    if (phase === 'OBJECTIVE_SETTING') return data.objectives || [];
    if (phase === 'MONITORING') return (data.objectives || []).map((objective) => data.reviews?.find((review) => review.phase === 'MONITORING' && review.objectiveId === objective.objectiveId && review.reviewId === reviewWindow.reviewId) || { objectiveId: objective.objectiveId, objectiveIndex: objective.objectiveIndex, reviewId: reviewWindow.reviewId, answers: {} });
    if (phase === 'FINAL_REVIEW') return (data.objectives || []).map((objective) => data.reviews?.find((review) => review.phase === 'FINAL_REVIEW' && review.objectiveId === objective.objectiveId) || { objectiveId: objective.objectiveId, objectiveIndex: objective.objectiveIndex, answers: {} });
    return data.cpdEntries || [];
  }, [data, phase, reviewWindow.reviewId]);

  function updateDraft(key, fieldId, value) {
    setDrafts((current) => ({ ...current, [key]: { ...(current[key] || {}), [fieldId]: value } }));
  }

  async function saveItem(item, index) {
    const key = itemKey(phase, item, index);
    setSaving(key);
    try {
      const response = await SEC_DataApiPost('perfmgr/objective', JSON.stringify({
        recordId,
        phase,
        objectiveId: item.objectiveId || '',
        reviewId: phase === 'MONITORING' ? reviewWindow.reviewId : '',
        entryId: item.entryId || '',
        answers: drafts[key] || {},
      }));
      if (!response || response.message) throw new Error(response?.message || 'Save failed');
      toast.success('✅ Section saved.', TOAST_CONFIG);
      await loadRecord();
    } catch (error) {
      console.error(error);
      toast.error('❌ Unable to save this section.', TOAST_CONFIG);
    } finally {
      setSaving('');
    }
  }

  async function addCpdEntry() {
    const key = `CPD-new-${Date.now()}`;
    setData((current) => ({ ...current, cpdEntries: [...(current.cpdEntries || []), { _draftKey: key, answers: {} }] }));
    setDrafts((current) => ({ ...current, [key]: {} }));
  }

  async function runAction(action) {
    try {
      const response = await SEC_DataApiPost('perfmgr/record/action', JSON.stringify({ recordId, action, comment: actionComment, reviewId: phase === 'MONITORING' ? reviewWindow.reviewId : '' }));
      if (!response || response.message) throw new Error(response?.message || 'Action failed');
      setActionComment('');
      toast.success('✅ Record updated.', TOAST_CONFIG);
      await loadRecord();
    } catch (error) {
      console.error(error);
      toast.error('❌ The record could not move to the next stage. Check all required fields are complete.', TOAST_CONFIG);
    }
  }

  if (loading) return <div className='performance-management-record'><p>Loading record…</p></div>;
  if (!data) return <div className='performance-management-record'><button type='button' onClick={() => navigate('../dashboard')}>Back to dashboard</button><p>This record is unavailable.</p></div>;

  const actions = availableActions(data.header?.status, data.viewerRole);
  return <div className='performance-management-record'>
    <header className='performance-management-record__header'>
      <div><button type='button' className='performance-management-record__back' onClick={() => navigate('../dashboard')}>Back to dashboard</button><h1 className='performance-management-record__title'>{data.header?.profileName}</h1><p className='performance-management-record__meta'>{data.header?.templateName} · {data.header?.staffName} · {statusLabel(data.header?.status)}</p></div>
    </header>
    <nav className='performance-management-lifecycle-tabs' aria-label='Record stages'>
      {PHASES.map((item) => <button type='button' key={item.id} className={phase === item.id ? 'active' : ''} onClick={() => setPhase(item.id)}>{item.label}</button>)}
    </nav>
    <section className='performance-management-stage-summary'>
      <h2>{phaseConfig.label}</h2>
      {phase === 'MONITORING' && (data.schedule?.monitoringWindows || []).length > 1 && <label>Review period<select value={reviewWindow.reviewId || ''} onChange={(event) => setSelectedReviewId(event.target.value)}>{data.schedule.monitoringWindows.map((window) => <option key={window.reviewId} value={window.reviewId}>{window.name}</option>)}</select></label>}
      <p>{phaseDueText(phase, data.schedule, reviewWindow)}</p>
    </section>
    {phase === 'CPD' && <button type='button' className='performance-management-record__objective-save' onClick={addCpdEntry}>Add CPD entry</button>}
    <div className='performance-management-stage-items'>
      {items.map((item, index) => {
        const key = item._draftKey || itemKey(phase, item, index);
        return <article className='performance-management-record__objective' key={key}>
          <h3>{phase === 'CPD' ? `CPD entry ${index + 1}` : `Objective ${item.objectiveIndex || index + 1}`}</h3>
          <div className='performance-management-record__fields'>
            {fields.map((field) => <LifecycleField key={field.id} field={field} role={data.viewerRole} value={drafts[key]?.[field.id] ?? ''} onChange={(value) => updateDraft(key, field.id, value)} />)}
          </div>
          <button type='button' className='performance-management-record__objective-save' disabled={saving === key} onClick={() => saveItem(item, index)}>{saving === key ? 'Saving…' : 'Save section'}</button>
        </article>;
      })}
      {items.length === 0 && phase !== 'CPD' && <p>No sections are configured for this stage.</p>}
    </div>
    {actions.length > 0 && <section className='performance-management-actions'>
      <h2>Move record forward</h2>
      <textarea value={actionComment} onChange={(event) => setActionComment(event.target.value)} placeholder='Optional comment for the audit history' />
      <div>{actions.map((action) => <button type='button' key={action.id} onClick={() => runAction(action.id)}>{action.label}</button>)}</div>
    </section>}
  </div>;
}

function buildDrafts(data) {
  const result = {};
  (data.objectives || []).forEach((item, index) => { result[itemKey('OBJECTIVE_SETTING', item, index)] = item.answers || {}; });
  (data.reviews || []).forEach((item, index) => { result[itemKey(item.phase, item, index)] = item.answers || {}; });
  (data.cpdEntries || []).forEach((item, index) => { result[itemKey('CPD', item, index)] = item.answers || {}; });
  return result;
}

function itemKey(phase, item, index) {
  if (item._draftKey) return item._draftKey;
  if (phase === 'CPD') return `CPD-${item.entryId || index}`;
  if (phase === 'MONITORING') return `MONITORING-${item.reviewId}-${item.objectiveId}`;
  return `${phase}-${item.objectiveId || index}`;
}

function LifecycleField({ field, role, value, onChange }) {
  const editable = role === 'ADMIN' || field.owner === 'BOTH' || field.owner === role;
  const common = { id: field.id, value: value ?? '', disabled: !editable, required: field.required === true, onChange: (event) => onChange(field.type === 'CHECKBOX' ? event.target.checked : event.target.value) };
  return <label className='performance-management-record__field'><span className='performance-management-record__label'>{field.label}{field.required ? ' *' : ''} <small>{field.owner === 'MANAGER' ? 'Manager' : field.owner === 'BOTH' ? 'Shared' : 'Staff'}</small></span>
    {field.type === 'TEXTAREA' ? <textarea className='performance-management-record__textarea' {...common} /> : field.type === 'DROPDOWN' ? <select className='performance-management-record__input' {...common}><option value=''>Select…</option>{(field.values || []).map((option) => <option key={option.id} value={option.text}>{option.text}</option>)}</select> : field.type === 'CHECKBOX' ? <input type='checkbox' checked={value === true} disabled={!editable} onChange={common.onChange} /> : <input className='performance-management-record__input' type={field.type === 'DATE' ? 'date' : field.type === 'NUMBER' ? 'number' : 'text'} {...common} />}
  </label>;
}

function availableActions(status, role) {
  const map = {
    STAFF: { DRAFT_OBJECTIVES: ['SUBMIT_OBJECTIVES', 'Submit objectives'], OBJECTIVES_RETURNED: ['SUBMIT_OBJECTIVES', 'Resubmit objectives'], OBJECTIVES_AGREED: ['SUBMIT_MONITORING', 'Submit monitoring review'], MONITORING_RETURNED: ['SUBMIT_MONITORING', 'Resubmit monitoring review'], MONITORING_COMPLETE: ['SUBMIT_FINAL', 'Submit final review'], FINAL_RETURNED: ['SUBMIT_FINAL', 'Resubmit final review'], AWAITING_STAFF_SIGNOFF: ['STAFF_SIGNOFF', 'Sign off and complete'] },
    MANAGER: { AWAITING_MANAGER_APPROVAL: [['APPROVE_OBJECTIVES', 'Approve objectives'], ['RETURN_OBJECTIVES', 'Return for changes']], MONITORING_AWAITING_MANAGER: [['APPROVE_MONITORING', 'Approve monitoring'], ['RETURN_MONITORING', 'Return monitoring']], FINAL_AWAITING_MANAGER: [['APPROVE_FINAL', 'Approve final review'], ['RETURN_FINAL', 'Return final review']] },
  };
  const value = map[role]?.[status];
  if (!value) return [];
  const rows = Array.isArray(value[0]) ? value : [value];
  return rows.map(([id, label]) => ({ id, label }));
}
function statusLabel(value) { return String(value || '').replaceAll('_', ' ').toLowerCase().replace(/^./, (letter) => letter.toUpperCase()); }
function phaseDueText(phase, schedule, window) {
  const due = phase === 'OBJECTIVE_SETTING' ? schedule?.objectiveSetting?.dueAt : phase === 'MONITORING' ? window?.dueAt : phase === 'FINAL_REVIEW' ? schedule?.finalReview?.dueAt : '';
  return due ? `Due ${new Date(due).toLocaleDateString('en-GB')}` : 'No due date has been configured.';
}
